import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import livly from "../../assets/logoLC.jpeg";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { navlinks, privateNavlinks } from "../../shared/utils/navRoutes";
import auth from "shared/Auth0";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isAdmin } from "shared/api/IsAdmin";
import { isSuperUser } from "shared/api/isSuperUser";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible";

type Props = {
  name: string;
  icon: string;
  handleClick: () => void;
};

interface IconProps extends Props {
  isActive: string;
  isExpandable?: boolean;
  isAdmin?: boolean;
  subRoute?: boolean;
}

const Logout = ({ name, icon, handleClick }: Props) => (
  <div
    className={`w-[200px] h-[40px] rounded-[10px] pt-2  flex justify-start items-center hover:cursor-pointer hover:text-blue-700`}
    onClick={handleClick}
  >
    <div className="flex items-center justify-start px-5">
      <FontAwesomeIcon
        icon={icon as IconProp}
        className={`text-2xl hover:text-blue-700 mr-4`}
      />
      <p className={`font-semibold`}>{name}</p>
    </div>
  </div>
);

const Icon = ({
  name,
  icon,
  isActive,
  isExpandable = false,
  isAdmin,
  subRoute = false,
  handleClick,
}: IconProps) => {
  return (
    <div
      className={`${subRoute && "border-l-2 pl-2 ml-2"} ${
        isActive && isActive === name ? "border-blue-500 " : "border-gray-200 "
      }`}
    >
      <div
        className={`${
          subRoute ? "w-[180px] h-[50px] " : "w-[200px] h-[50px]  xl:h-[60px]"
        }  rounded-[10px] ${
          isActive && isActive === name && "bg-blue-100"
        } flex justify-start items-center   hover:cursor-pointer`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between px-5">
          <div className="flex items-center justify-start">
            <FontAwesomeIcon
              icon={icon as IconProp}
              className={`text-2xl text-blue-700   ${
                isActive !== name && "grayscale"
              } mr-4`}
            />
            <p
              className={` text-blue-700 ${
                isActive !== name ? "grayscale" : "font-bold"
              } ${isAdmin && "mr-8"}`}
            >
              {name}
            </p>
          </div>
          {isAdmin && (
            <div
              style={{
                transition: "transform 300ms ease",
                transform: isExpandable ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <FontAwesomeIcon
                icon={"chevron-right"}
                className={`text-lg text-blue-700   ${
                  isActive !== name && "grayscale"
                }  transition-all`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState("Home");
  const [isExpandable, setIsExpendable] = useState(false);
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);

  const privateNavs = privateNavlinks().filter(
    (link) => !link.requiresSuperUser || isSuperUserPermission,
  );
  const visibleNavlinks = navlinks.filter(
    (link) => !link.requiresSuperUser || isSuperUserPermission,
  );

  return (
    <div className="flex justify-start items-center flex-col sticky top-4 h-[95vh] xl:h-[90vh] ml-4">
      <Link to="/">
        <div
          className={`w-[220px] h-20 rounded-[10px] bg-white relative overflow-hidden flex justify-center items-center shadow`}
        >
          <div>
            <div className="flex items-center">
              <img
                src={livly}
                alt="logo"
                width={25}
                style={{ marginRight: "10px" }}
              />
              <p>
                <strong>LivlyConnect.</strong>
              </p>
            </div>
            <div className="mt-1 mr-1 float-right w-12 h-1 bg-[#5FA5F8]" />
          </div>
        </div>
      </Link>
      <div className="flex-1 flex flex-col justify-between items-center bg-white rounded-[20px] w-[220px] py-4 mt-10 shadow">
        <div className="flex flex-col items-center justify-center gap-2">
          {visibleNavlinks.slice(0, 1).map((link) => (
            <Icon
              key={link.name}
              {...link}
              isActive={isActive}
              handleClick={() => {
                setIsActive(link.name);
                navigate(link.link);
              }}
            />
          ))}
          {isAdmin(user) && (
            <>
              <Collapsible
                onOpenChange={(open) => setIsExpendable(open)}
                open={isExpandable}
              >
                <div
                  className={`${
                    isActive === "Admin" ? "border-blue-500" : "border-gray-200"
                  }`}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className={`flex h-[50px] w-[200px] items-center justify-start rounded-[10px] border-0 bg-transparent p-0 text-left xl:h-[60px] ${
                        isActive === "Admin" ? "bg-blue-100" : ""
                      }`}
                      onClick={() => setIsActive("Admin")}
                      type="button"
                    >
                      <div className="flex items-center justify-between px-5">
                        <div className="flex items-center justify-start">
                          <FontAwesomeIcon
                            aria-hidden="true"
                            className={`mr-4 text-2xl text-blue-700 ${
                              isActive !== "Admin" ? "grayscale" : ""
                            }`}
                            icon="user-gear"
                          />
                          <span
                            className={`mr-8 text-blue-700 ${
                              isActive !== "Admin" ? "grayscale" : "font-bold"
                            }`}
                          >
                            Admin
                          </span>
                        </div>
                        <span
                          className={`transition-transform duration-300 ${
                            isExpandable ? "rotate-90" : "rotate-0"
                          }`}
                        >
                          <FontAwesomeIcon
                            aria-hidden="true"
                            className={`text-lg text-blue-700 ${
                              isActive !== "Admin" ? "grayscale" : ""
                            }`}
                            icon="chevron-right"
                          />
                        </span>
                      </div>
                    </button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <div>
                    {privateNavs.map((link) => (
                      <Icon
                        key={link.name}
                        {...link}
                        isActive={isActive}
                        subRoute={true}
                        handleClick={() => {
                          setIsActive(link.name);
                          navigate(link.link);
                        }}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
          {visibleNavlinks.slice(1, visibleNavlinks.length).map((link) => (
            <Icon
              key={link.name}
              {...link}
              isActive={isActive}
              handleClick={() => {
                setIsActive(link.name);
                navigate(link.link);
              }}
            />
          ))}
        </div>
        <div className="border-t border-gray-300">
          <Logout
            name={"Logout"}
            icon={"right-from-bracket"}
            handleClick={() => auth.logout()}
          />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
